import { create } from 'zustand';
import { io } from 'socket.io-client';
import { getAuthAndUser } from 'utils/auth-user-storage';

const useSocketStore = create((set, get) => ({
  socket: null,
  isConnected: false,
  isReconnecting: false,

  initializeSocket: async (isReconnectAttempt = false) => {
    const { authToken } = await getAuthAndUser();
    if (!authToken) return;

    get().disconnectSocket();

    const socket = io(process.env.REACT_APP_BASE_URL || '', {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      randomizationFactor: 0.5,
      transports: ['websocket'],
      reconnectionDelayMax: 5000,
      query: { token: authToken },
      reconnectionAttempts: Infinity,
    });

    // Connection events
    socket.on('connect', () => {
      set({ isConnected: true, isReconnecting: isReconnectAttempt });
    });

    socket.on('disconnect', (reason) => {
      set({ isConnected: false });

      // If disconnect was due to server restart or transport close, try to reconnect
      if (reason === 'io server disconnect' || reason === 'transport close') {
        socket.connect();
      }
    });

    const handleOnline = () => {
      const { socket, isConnected, initializeSocket } = get();
      if (!isConnected && socket.disconnected) {
        initializeSocket(true);
      }
    };

    const handleOffline = () => {
      ('Browser went offline');
      set({ isConnected: false, isReconnecting: false });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    set({
      socket,
      cleanupListeners: () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      },
    });
  },

  disconnectSocket: () => {
    const { socket, cleanupListeners } = get();
    if (cleanupListeners) cleanupListeners();
    if (socket) {
      socket.off('connect');
      socket.off('disconnect');
      socket.disconnect();
      set({
        socket: null,
        isConnected: false,
        isReconnecting: false,
      });
    }
  },
}));

export default useSocketStore;
